import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  NavigationGuardNext,
  RouteLocationNormalized,
} from 'vue-router'
import { useUserStore } from '../stores/user'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

// Track redirect attempts to prevent infinite loops
const redirectAttempts = new Map<string, number>()
const MAX_REDIRECT_ATTEMPTS = 3

// Permission guard function with loop prevention
const requiresPermission = (permissions: string[], requireAll = false) => {
  return async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const userStore = useUserStore()

    // Prevent infinite redirect loops
    const redirectKey = `${from.path}->${to.path}`
    const attempts = redirectAttempts.get(redirectKey) || 0

    if (attempts >= MAX_REDIRECT_ATTEMPTS) {
      console.error('Max redirect attempts reached, breaking loop')
      redirectAttempts.clear()
      return next({ name: 'dashboard' })
    }

    redirectAttempts.set(redirectKey, attempts + 1)

    // Check if user is authenticated first
    if (!userStore.isAuthenticated) {
      // Try to restore from storage
      const restored = await userStore.initializeFromStorage()

      if (!restored) {
        redirectAttempts.clear()

        // Only redirect to login if we're not already going there
        if (to.name !== 'dashboard') {
          return next({
            name: 'dashboard',
            query: { redirect: to.fullPath },
          })
        }
        return next()
      }
    }

    // Super users bypass all permission checks
    if (userStore.user?.isSuperUser) {
      redirectAttempts.clear()
      return next()
    }

    // Check permissions
    let hasPermission = false
    if (requireAll) {
      hasPermission = userStore.hasAllPermissions(permissions)
    } else {
      hasPermission = userStore.hasAnyPermission(permissions)
    }

    if (hasPermission) {
      redirectAttempts.clear()
      next()
    } else {
      console.warn(`User lacks required permissions for route: ${to.path}`, {
        required: permissions,
        userPermissions: userStore.userPermissions,
      })

      redirectAttempts.clear()

      // Redirect to dashboard if user doesn't have permissions
      next({ name: 'dashboard' })
    }
  }
}

// Authentication guard with loop prevention
const requiresAuth = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const userStore = useUserStore()

  // Prevent infinite loops
  if (to.name === from.name && to.path === from.path) {
    return next()
  }

  if (!userStore.isAuthenticated) {
  //  const restored = await userStore.initializeFromStorage()

  //   if (!restored) {
  //     // Only redirect to login if we're not already there
  //     if (to.name !== 'login') {
  //       return next({
  //         name: 'login',
  //         query: { redirect: to.fullPath },
  //       })
  //     }
  //      next()
  //   }
  }

  next()
}

const routes: Array<RouteRecordRaw> = [
  // Root redirect
  {
    path: '/',
    redirect: '/dashboard',
  },

  // Auth routes
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
        meta: { public: true, title: 'Login' },
      },
    ],
  },

  // Main app routes
  {
    path: '/',
    component: AppLayout,
    children: [
      // Dashboard
      {
        name: 'dashboard',
        path: 'dashboard',
        beforeEnter: requiresPermission(['view_admin', 'view_user', 'view_hr', 'view_leader','view_leader_admin', 'view_hr_admin']),
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),       
        meta: {
          title: 'Dashboard',
          requiresKrmId: false,
        },
      },

      // PowerBI Reports
      {
        name: 'user-dashboard',
        path: 'powerbi-reports/:codename/:name/:pageName?',
        component: () => import('../pages/powerbi/UserReportPage.vue'),
        beforeEnter: requiresPermission(['view_user']),
        props: true,
        meta: {
          title: 'User',
          requiresKrmId: false,
        },
      },

      // Direct Component
      {
        name: 'hr-dashboard',
        path: 'exec-reports/:codename/:name/:pageName?',
        component: () => import('../pages/powerbi/HRReportPage.vue'),
        beforeEnter: requiresPermission(['view_hr']),
        props: true,
        meta: {
          title: 'HR',
          requiresKrmId: false,
        },
      },
      {
        name: 'hr-admin-dashboard',
        path: 'exec-reports/:codename/:name/:pageName?',
        component: () => import('../pages/powerbi/HRAdminReportPage.vue'),
        beforeEnter: requiresPermission(['view_hr_admin', 'view_leader_admin']),
        props: true,
        meta: {
          title: 'HR ADMIN',
          requiresKrmId: false,
        },
      },

      // Leads/ATLS Reports - Direct component
      {
        name: 'leads-dashboard',
        path: 'leads-reports/:codename/:name/:pageName?',
        component: () => import('../pages/powerbi/LeadsReportPage.vue'),
        beforeEnter: requiresPermission(['view_leader']),
        props: true,
        meta: {
          title: 'Leads',
          requiresKrmId: false,
        },
      },
      {
        name: 'leads-admin-dashboard',
        path: 'leads-reports/:codename/:name/:pageName?',
        component: () => import('../pages/powerbi/LeadsAdminReportPage.vue'),
        beforeEnter: requiresPermission(['view_leader_admin', 'view_hr_admin']),
        props: true,
        meta: {
          title: 'Leader Admin',
          requiresKrmId: false,
        },
      },

      // Admin Interface
      {
        name: 'user-admin',
        path: 'admin/users',
        beforeEnter: requiresPermission(['view_admin']),
        component: () => import('../pages/admin/pages/user/UsersPage.vue'),
        meta: { title: 'User Management', public: false}
      },

      {
        name: 'group-admin',
        path: 'admin/groups',
        beforeEnter: requiresPermission(['view_admin']),
        component: () => import('../pages/admin/pages/group/GroupPage.vue'),
        meta: { title: 'Group Management', public: false}
      },
       {
        name: 'permission-admin',
        path: 'admin/permissions',
        beforeEnter: requiresPermission(['view_admin']),
        component: () => import('../pages/admin/pages/permission/PermissionPage.vue'),
        meta: { title: 'Permission Management', public: false}
      },
       {
        name: 'super-admin',
        path: 'admin/super-user-management',
        beforeEnter: requiresPermission(['view_admin']),
        component: () => import('../pages/admin/pages/superadmin/SuperAdminPage.vue'),
        meta: { title: 'Super User Management', public: false}
      },
      {
        name: 'report-sidebar-admin',
        path: 'admin/report-sidebar',
        beforeEnter: requiresPermission(['view_admin']),
        component: () => import('../pages/admin/pages/reportsidebar/ReportSidebarPage.vue'),
        meta: { title: 'Sidebar Pages Management', public: false}
      },
      {
        name: 'report-admin',
        path: 'admin/reports',
        beforeEnter: requiresPermission(['view_admin']),
        component: () => import('../pages/admin/pages/reportmanagement/ReportManagementPage.vue'),
        meta: { title: 'Report Management', public: false}
      },
      {
        name: 'report-category-admin',
        path: 'admin/report-categories',
        beforeEnter: requiresPermission(['view_admin']),
        component: () => import('../pages/admin/pages/reportcategory/ReportCategoryPage.vue'),
        meta: { title: 'Report Categories', public: false}
      },

      // Preferences
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        beforeEnter: requiresAuth,
        meta: { title: 'Preferences' },
      },

      // FAQ
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
        meta: { title: 'FAQ', public: true },
      },
    ],
  },

  // 404 Page
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
    meta: { public: true, title: 'Page Not Found' },
  },

  // Catch all - redirect to 404
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Global navigation guard with loop prevention
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()

  // Clear redirect attempts on successful navigation
  if (from.path !== to.path) {
    redirectAttempts.clear()
  }

  // Prevent navigating to the same route
  if (to.path === from.path && to.name === from.name) {
    return next(false)
  }

  // Set page title
  const appTitle = 'PerformX Portal'
  if (to.meta.title) {
    document.title = `${to.meta.title} - ${appTitle}`
  } else {
    document.title = appTitle
  }

  // Public routes don't need authentication
  if (to.meta.public) {
    return next()
  }

  // Special handling for login route
  if (to.name === 'login') {
    // If already authenticated, redirect to dashboard
    if (userStore.isAuthenticated) {
      return next({ name: 'dashboard' })
    }
    return next()
  }
  // Check authentication for protected routes
  // if (!userStore.isAuthenticated && to.name !== 'login' && to.name !== '404') {
  //   const restored = await userStore.initializeFromStorage()

  //   if (!restored) {
  //     return next({
  //       name: 'login',
  //       query: { redirect: to.fullPath },
  //     })
  //   }
  // }

  // If route requires KRM ID, ensure it's available
  if (to.meta.requiresKrmId && userStore.user?.krm_id) {
    // Store KRM ID in route params for PowerBI integration
    to.params.krmId = String(userStore.user.krm_id)
  }

  next()
})

// After navigation hook for logging/analytics
router.afterEach((to, from) => {
  const userStore = useUserStore()
  console.log(userStore)

  // Log navigation for audit purposes (only in development)
  if (import.meta.env.DEV && userStore.isAuthenticated && userStore.user) {
    console.log(`Navigation: ${from.path} -> ${to.path}`)
  }
})

// Handle router errors
router.onError((error) => {
  console.error('Router error:', error)

  // Clear redirect attempts on error
  redirectAttempts.clear()

  // If it's a chunk loading error (code splitting), reload the page
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    window.location.reload()
  }
})

export default router
